import type { Freshness, Job, WorkType } from "../types/job";

interface MockSeed {
  id: string;
  title: string;
  normalizedTitle: string;
  company: string;
  city: string;
  region: string | null;
  country: string;
  latitude: number;
  longitude: number;
  workType: WorkType;
  eligibility?: string;
  salaryMin: number;
  salaryMax: number;
  ageHours: number;
  source: string;
  industry: string;
}

const seeds: MockSeed[] = [
  { id: "mock_northstar", title: "Senior Growth Marketing Manager", normalizedTitle: "Growth Marketing Manager", company: "Northstar Systems", city: "Toronto", region: "Ontario", country: "Canada", latitude: 43.6532, longitude: -79.3832, workType: "hybrid", salaryMin: 98000, salaryMax: 124000, ageHours: 5, source: "Greenhouse", industry: "Technology" },
  { id: "mock_lumen", title: "AI Content Strategist", normalizedTitle: "AI Content Strategist", company: "Lumen AI", city: "Berlin", region: "Berlin", country: "Germany", latitude: 52.52, longitude: 13.405, workType: "remote", eligibility: "Worldwide", salaryMin: 108000, salaryMax: 135000, ageHours: 19, source: "Ashby", industry: "Artificial Intelligence" },
  { id: "mock_harbour", title: "Content Marketing Manager", normalizedTitle: "Content Marketing Manager", company: "Harbour House", city: "St. Catharines", region: "Ontario", country: "Canada", latitude: 43.1594, longitude: -79.2469, workType: "onsite", salaryMin: 78000, salaryMax: 93000, ageHours: 30, source: "Lever", industry: "Tourism" },
  { id: "mock_arc", title: "Digital Marketing Lead", normalizedTitle: "Digital Marketing Lead", company: "Arc Health", city: "Miami", region: "Florida", country: "United States", latitude: 25.7617, longitude: -80.1918, workType: "hybrid", salaryMin: 112340, salaryMax: 143850, ageHours: 61, source: "Greenhouse", industry: "Healthcare" },
  { id: "mock_kinetic", title: "SEO & GEO Manager", normalizedTitle: "SEO & GEO Manager", company: "Kinetic Commerce", city: "Buffalo", region: "New York", country: "United States", latitude: 42.8864, longitude: -78.8784, workType: "onsite", salaryMin: 101380, salaryMax: 126040, ageHours: 82, source: "Workable", industry: "Retail" },
  { id: "mock_orbit", title: "Head of Content", normalizedTitle: "Head of Content", company: "Orbit Ledger", city: "Singapore", region: null, country: "Singapore", latitude: 1.3521, longitude: 103.8198, workType: "remote", eligibility: "Worldwide", salaryMin: 113300, salaryMax: 149350, ageHours: 116, source: "Employer Careers", industry: "Finance" },
  { id: "mock_fieldnote", title: "Brand Marketing Manager", normalizedTitle: "Brand Marketing Manager", company: "Fieldnote", city: "New York", region: "New York", country: "United States", latitude: 40.7128, longitude: -74.006, workType: "hybrid", salaryMin: 123300, salaryMax: 161660, ageHours: 145, source: "SmartRecruiters", industry: "Professional Services" },
  { id: "mock_purple", title: "Marketing Operations Manager", normalizedTitle: "Marketing Operations Manager", company: "Purple Current", city: "London", region: "England", country: "United Kingdom", latitude: 51.5072, longitude: -0.1276, workType: "remote", eligibility: "Europe and Americas", salaryMin: 92000, salaryMax: 116000, ageHours: 42, source: "Lever", industry: "SaaS" },
  { id: "mock_solar", title: "AI Marketing Specialist", normalizedTitle: "AI Marketing Specialist", company: "Solar Thread", city: "Sydney", region: "New South Wales", country: "Australia", latitude: -33.8688, longitude: 151.2093, workType: "remote", eligibility: "Worldwide", salaryMin: 88000, salaryMax: 104000, ageHours: 11, source: "Ashby", industry: "Consumer Products" },
  { id: "mock_signal", title: "Content Strategy Manager", normalizedTitle: "Content Strategy Manager", company: "Signal Works", city: "Hamilton", region: "Ontario", country: "Canada", latitude: 43.2557, longitude: -79.8711, workType: "onsite", salaryMin: 89000, salaryMax: 109000, ageHours: 73, source: "Employer Careers", industry: "Manufacturing" },
  { id: "mock_meridian", title: "Marketing Communications Manager", normalizedTitle: "Marketing Communications Manager", company: "Meridian Labs", city: "Lisbon", region: "Lisbon", country: "Portugal", latitude: 38.7223, longitude: -9.1393, workType: "remote", eligibility: "Worldwide", salaryMin: 81000, salaryMax: 97000, ageHours: 7, source: "Greenhouse", industry: "Education" },
  { id: "mock_mosaic", title: "Digital Marketing Strategist", normalizedTitle: "Digital Marketing Strategist", company: "Mosaic Studio", city: "Tokyo", region: "Tokyo", country: "Japan", latitude: 35.6762, longitude: 139.6503, workType: "remote", eligibility: "Global async", salaryMin: 94000, salaryMax: 121000, ageHours: 156, source: "Employer Careers", industry: "Media" },
];
function freshness(ageHours: number): Freshness {
  if (ageHours <= 24) return "hot";
  if (ageHours <= 72) return "fresh";
  return "fading";
}

export function getMockJobs(now = new Date()): Job[] {
  return seeds.map((seed) => {
    const postedAt = new Date(now.getTime() - seed.ageHours * 3_600_000);
    const discoveredAt = new Date(postedAt.getTime() + Math.min(2, seed.ageHours / 2) * 3_600_000);
    return {
      id: seed.id,
      title: seed.title,
      normalizedTitle: seed.normalizedTitle,
      company: seed.company,
      companyWebsite: `https://${seed.company.toLowerCase().replace(/[^a-z0-9]+/g, "")}.example`,
      location: {
        city: seed.city,
        region: seed.region,
        country: seed.country,
        latitude: seed.latitude,
        longitude: seed.longitude,
      },
      eligibility: seed.eligibility ?? null,
      workType: seed.workType,
      employmentType: "full_time",
      salary: {
        currency: "CAD",
        minimum: seed.salaryMin,
        maximum: seed.salaryMax,
        cadMinimum: seed.salaryMin,
        cadMaximum: seed.salaryMax,
        status: "verified",
      },
      postedAt: postedAt.toISOString(),
      discoveredAt: discoveredAt.toISOString(),
      ageHours: seed.ageHours,
      freshness: freshness(seed.ageHours),
      source: seed.source,
      applicationUrl: `https://example.com/jobs/${seed.id}`,
      industry: seed.industry,
    };
  });
}
